import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Avatar,
} from "@mui/material";
import {
  useDeleteUserMutation,
  useGetUserAdminOnyQuery,
  useGetUserInfoOnyQuery,
  useGetUserRoleQuery,
  useGetRestaurantQuery,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import {
  IUserAdmin,
  IUserRole,
  IRestaurant,
  ITeam,
  ITeam2,
} from "../../../Api/Interface/api.interface";
import NewUpdatePrivi from "../Edit/index";
import ChangePassword from "../ChangePassword/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import Pagination from "../../../Components/Pagination";
import PasswordIcon from "@mui/icons-material/Password";
import { capitalizeFirstLetter } from "../../../Components/Validation/capitalizeFirstLetter";
import useIsMobile from "../../../Core/hook/useIsMobile";
import GroupIcon from "@mui/icons-material/Group";
import { toProperCase } from "../../../Components/Validation/toProperCase";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const isMobileRes = useIsMobile();

  const [selectedCoursing, setSelectedCoursing] = useState<IUserAdmin | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState(false);

  const [selectedChangePassword, setSelectedChangePassword] =
    useState<IUserAdmin | null>(null);
  const [openDialogChangePassword, setOpenDialogChangePassword] =
    useState(false);

  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IUserAdmin | null>(
    null
  );

  const { data, isLoading, isError, refetch } = useGetUserAdminOnyQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetUserRoleQuery({
      searchText: "",
    });
  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const { data: restaurantData, isLoading: restauramtIsLoading } =
    useGetRestaurantQuery({
      searchText: "",
      id: "",
    });

  const { data: staffData, isLoading: staffIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const userroleList = useMemo(() => {
    return userroleData?.data as IUserRole[];
  }, [userroleData?.data]);

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const restaurantList = useMemo(() => {
    return restaurantData?.data as IRestaurant[];
  }, [restaurantData?.data]);

  const staffList = useMemo(() => {
    return staffData?.data as ITeam2[];
  }, [staffData?.data]);

  const userroleMap = useMemo(() => {
    const map = new Map();
    userroleList?.forEach((category) => {
      map.set(category.id, category.roleName);
    });
    return map;
  }, [userroleList]);

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((user) => {
      map.set(user.id, `${user.user_role_name} - ${user.username}`);
    });
    return map;
  }, [userList]);

  const restaurantMap = useMemo(() => {
    const map = new Map();
    restaurantList?.forEach((category) => {
      map.set(category.id, category.restaurantsName);
    });
    return map;
  }, [restaurantList]);

  const staffMap = useMemo(() => {
    const map = new Map();
    staffList?.forEach((staff) => {
      const fullName = `${capitalizeFirstLetter(
        staff.teamFirstName
      )} ${capitalizeFirstLetter(staff.teamLastName)}`;
      map.set(staff.id, fullName);
    });
    return map;
  }, [staffList]);

  const customerMap = useMemo(() => {
    const map = new Map<
      string,
      { fullName: string; email: string; phoneNumber: string; img: string }
    >();

    staffList?.forEach((customer) => {
      map.set(customer.id, {
        fullName:
          toProperCase(customer.teamFirstName || "") +
          " " +
          toProperCase(customer.teamLastName || ""),
        email: customer.teamEmail || "",
        phoneNumber: customer.teamMobileNum || "",
        img: customer.teamImg || "",
      });
    });

    return map;
  }, [staffList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDialog = (coursing: IUserAdmin) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDialogChangePassword = (coursing: IUserAdmin) => {
    setSelectedChangePassword(coursing);
    setOpenDialogChangePassword(true);
  };

  const handleCloseDialogChangePassword = () => {
    setSelectedChangePassword(null);
    setOpenDialogChangePassword(false);
  };

  const handleOpenDeletePopup = (coursing: IUserAdmin) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteUserMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the user");
    }
  };

  const coursings: IUserAdmin[] = Array.isArray(data?.data)
    ? (data?.data as IUserAdmin[])
    : [];

  const handleAddProductClick = () => {
    navigate("/user/create");
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        {/* <Grid item lg={4} md={4} sm={4} xs={4} pt={2} alignContent={"center"}>
          <Button
            variant="contained"
            className={classes.addNewButton}
            onClick={handleAddProductClick}
          >
            Add New
          </Button>
        </Grid> */}
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{ display: "flex" }}
          >
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: {
                    root: classes.formControlList,
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="User Name  .."
                className={classes.searchTextField}
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                // variant="contained"
                className={classes.addNewButton}
                onClick={handleAddProductClick}
              >
                Add New
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}>
                  <strong>User Name</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  // sx={{ textAlign: "center" }}
                >
                  <strong>User Role</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  // sx={{ textAlign: "center" }}
                >
                  <strong>Branch</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  // sx={{ textAlign: "center" }}
                >
                  <strong>Create By</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  sx={{ textAlign: "right" }}
                >
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || userroleIsLoading || userIsLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10vh"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || coursings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                (coursings || [])
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: IUserAdmin, index) => {
                    const customer = customerMap.get(row.staffId);
                    return (
                      <TableRow key={row.id} className={classes.tableRowHover}>
                        <TableCell
                          component="th"
                          scope="row"
                          className={classes.tablCell}
                        >
                          <Grid container spacing={1}>
                            {!isMobileRes && (
                              <Grid item>
                                {customer?.img ? (
                                  <Avatar
                                    alt="Img"
                                    src={customer.img}
                                    className={classes.avatatImage}
                                    sx={{
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  <Avatar className={classes.avatatImage}>
                                    {row?.username ? (
                                      `${row?.username ?? ""}`
                                        .trim()
                                        .charAt(0)
                                        .toUpperCase()
                                    ) : (
                                      <GroupIcon />
                                    )}
                                  </Avatar>
                                )}
                              </Grid>
                            )}
                            <Grid item textAlign={"left"} alignContent={"center"}>
                              <Typography className={classes.nameTableList}>
                                {row.username ? `${row.username}` : ""}
                              </Typography>
                              <Link
                                to={`/team-history/${row.staffId}`}
                                className={classes.iconLinkPropNormal}
                              >
                                {customer && (
                                  <Typography
                                    className={classes.mobileListText}
                                  >
                                    {customer ? `${customer.fullName}` : ""}
                                  </Typography>
                                )}
                              </Link>
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {userroleMap.get(row.user_role) || ""}
                        </TableCell>
                        <TableCell
                          scope="row"
                          className={classes.tablCell}
                          // sx={{ textAlign: "center" }}
                        >
                          {Array.isArray(row.branchIds) &&
                          row.branchIds.length > 0 ? (
                            row.branchIds.map(
                              (branchId: number, index: number) => (
                                <span key={branchId}>
                                  {restaurantMap.get(branchId) || ""}
                                  {index < row.branchIds.length - 1 && ", "}
                                </span>
                              )
                            )
                          ) : (
                            <span></span>
                          )}
                        </TableCell>

                        <TableCell
                          scope="row"
                          className={classes.tablCell}
                          // sx={{ textAlign: "center" }}
                        >
                          {userMap.get(row.created_by) || ""}
                        </TableCell>
                        <TableCell className={classes.tablCell}>
                          <Grid
                            container
                            spacing={1}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.viewButtonList}
                                onClick={() =>
                                  handleOpenDialogChangePassword(row)
                                }
                              >
                                <PasswordIcon
                                  className={classes.viewListIcon}
                                />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.editButtonList}
                                onClick={() => handleOpenDialog(row)}
                              >
                                <EditSharpIcon
                                  className={classes.editListIcon}
                                />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.deleteButtonList}
                                onClick={() => handleOpenDeletePopup(row)}
                              >
                                <DeleteOutlineSharpIcon
                                  className={classes.deleteListIcon}
                                />
                              </Button>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer>

        {selectedChangePassword && (
          <ChangePassword
            openModel={openDialogChangePassword}
            handleCloseDialog={handleCloseDialogChangePassword}
            data={selectedChangePassword}
          />
        )}

        {selectedCoursing && (
          <NewUpdatePrivi
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(coursingToDelete.id.toString());
            }}
            title="Delete Position"
            content={`Are you sure you want to delete "${coursingToDelete.username}"?`}
          />
        )}
      </Grid>
    </Card>
  );
};

export default Index;
